import { z } from 'zod';
import {
  isValidIban,
  LOCAL_CODE_BY_COUNTRY,
  normalizeLocalCode,
  normalizePixKey,
  PIX_KEY_TYPES,
} from './partner-bank.ts';
import { isKnownCountry } from './partners.ts';
import { countryCode } from './schemas.ts';

/**
 * Contas bancárias das empresas do grupo (Tesouraria, ADR 0073). Não confundir com as contas dos
 * parceiros (ADR 0016): estas são da LOOT, movimentam o caixa e têm finalidade, vigência e canal de
 * integração. O número da conta ou o IBAN é cifrado pela API e só aparece mascarado.
 */

export const TREASURY_ACCOUNT_KINDS = [
  'br_account',
  'iban',
  'us_account',
  'foreign_account',
] as const;
export type TreasuryAccountKind = (typeof TREASURY_ACCOUNT_KINDS)[number];

export const TREASURY_ACCOUNT_TYPES = [
  'checking',
  'savings',
  'payment',
  'investment',
  'other',
] as const;

export const TREASURY_ACCOUNT_PURPOSES = [
  'operating',
  'collection',
  'payments',
  'payroll',
  'investment',
  'tax',
  'fx',
  'cards',
  'other',
] as const;
export type TreasuryAccountPurpose = (typeof TREASURY_ACCOUNT_PURPOSES)[number];

export const TREASURY_INTEGRATION_CHANNELS = ['manual', 'file', 'api', 'open_finance'] as const;

const text = (max: number, min = 1) => z.string().trim().min(min).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const upperNoSpaces = (re: RegExp, message: string) =>
  z
    .string()
    .transform((v) => v.replace(/[\s.]+/g, '').toUpperCase())
    .pipe(z.string().regex(re, message));
const swiftBic = upperNoSpaces(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'SWIFT/BIC inválido');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');

const common = {
  legalEntityId: z.uuid(),
  nickname: text(80, 2),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Moeda inválida'),
  bankName: text(200),
  purpose: z.enum(TREASURY_ACCOUNT_PURPOSES),
  validFrom: isoDate,
  validUntil: isoDate.nullable().optional(),
  integrationChannel: z.enum(TREASURY_INTEGRATION_CHANNELS).default('manual'),
  integrationNotes: optionalText(500),
  responsibleUserId: z.uuid().nullable().optional(),
  /** Por que a conta está sendo incluída ou alterada (vai para o histórico). */
  changeReason: text(1000, 3),
};

/**
 * Conta fora do Brasil (revisão de 29/10/2026, como a dos fornecedores): endereço do banco e, se
 * houver, o banco intermediário (SWIFT, nome e a conta do banco da empresa no intermediário).
 */
const abroad = {
  bankAddress: optionalText(300),
  intermediary: z
    .object({
      swiftBic,
      name: text(200),
      account: upperNoSpaces(/^[A-Z0-9]{1,34}$/, 'Conta no intermediário inválida')
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
};

/** Moeda sugerida por tipo de conta; IBAN e outro país pedem a escolha (vazio). */
export const TREASURY_KIND_CURRENCY: Record<TreasuryAccountKind, string> = {
  br_account: 'BRL',
  iban: '',
  us_account: 'USD',
  foreign_account: '',
};

/** Código local que o país exige quando a conta não é IBAN (os EUA têm o routing number). */
export function treasuryLocalCodeType(country: string) {
  const type = LOCAL_CODE_BY_COUNTRY[country];
  return type && type !== 'aba' ? type : null;
}

export const treasuryBankAccountInput = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('br_account'),
      ...common,
      bankCode: z
        .string()
        .trim()
        .regex(/^[0-9]{3}$/, 'Código do banco com 3 dígitos'),
      ispb: z
        .string()
        .trim()
        .regex(/^[0-9]{8}$/, 'ISPB com 8 dígitos')
        .nullable()
        .optional(),
      branch: upperNoSpaces(/^[0-9]{1,5}(-[0-9A-Z])?$/, 'Agência inválida'),
      accountNumber: upperNoSpaces(/^[0-9]{1,20}-?[0-9A-Z]$/, 'Conta inválida'),
      accountType: z.enum(TREASURY_ACCOUNT_TYPES),
      // Chave PIX para receber, com o tipo, como nas contas dos fornecedores.
      pixKeyType: z.enum(PIX_KEY_TYPES).nullable().optional(),
      pixKey: optionalText(100),
    }),
    z.object({
      kind: z.literal('iban'),
      ...common,
      iban: upperNoSpaces(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/, 'IBAN inválido').refine(
        isValidIban,
        'IBAN inválido',
      ),
      swiftBic: swiftBic.nullable().optional(),
      ...abroad,
    }),
    z.object({
      kind: z.literal('us_account'),
      ...common,
      routingNumber: z
        .string()
        .trim()
        .regex(/^[0-9]{9}$/, 'Routing number com 9 dígitos'),
      accountNumber: upperNoSpaces(/^[0-9]{4,17}$/, 'Conta inválida'),
      accountType: z.enum(TREASURY_ACCOUNT_TYPES),
      swiftBic: swiftBic.nullable().optional(),
      ...abroad,
    }),
    z
      .object({
        kind: z.literal('foreign_account'),
        ...common,
        ...abroad,
        country: countryCode.refine(isKnownCountry, 'País inválido'),
        swiftBic,
        accountNumber: upperNoSpaces(/^[A-Z0-9]{1,34}$/, 'Conta inválida'),
        accountType: z.enum(TREASURY_ACCOUNT_TYPES).nullable().optional(),
        localCode: optionalText(20),
      })
      .superRefine((v, ctx) => {
        // Reino Unido, Austrália, Índia e Canadá: o código local do banco é obrigatório.
        const type = treasuryLocalCodeType(v.country);
        if (type && !(v.localCode && normalizeLocalCode(type, v.localCode))) {
          ctx.addIssue({
            code: 'custom',
            path: ['localCode'],
            message:
              'Código local do banco obrigatório no país (sort code, BSB, IFSC ou do Canadá)',
          });
        }
      }),
  ])
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    path: ['validUntil'],
    message: 'O fim da vigência não pode ser antes do início',
  })
  .superRefine((v, ctx) => {
    if (v.kind !== 'br_account' || !v.pixKey) return;
    if (!v.pixKeyType) {
      ctx.addIssue({
        code: 'custom',
        path: ['pixKeyType'],
        message: 'Informe o tipo da chave PIX',
      });
    } else if (!normalizePixKey(v.pixKeyType, v.pixKey)) {
      ctx.addIssue({
        code: 'custom',
        path: ['pixKey'],
        message: 'Chave PIX inválida para o tipo escolhido',
      });
    }
  });
export type TreasuryBankAccountInput = z.infer<typeof treasuryBankAccountInput>;

/** O que a conta guarda cifrado (número ou IBAN), o país e os últimos caracteres. */
export function treasuryBankAccountSecret(input: TreasuryBankAccountInput): {
  secret: string;
  last4: string;
  country: string;
} {
  switch (input.kind) {
    case 'br_account':
      return { secret: input.accountNumber, last4: input.accountNumber.slice(-4), country: 'BR' };
    case 'iban':
      return { secret: input.iban, last4: input.iban.slice(-4), country: input.iban.slice(0, 2) };
    case 'us_account':
      return { secret: input.accountNumber, last4: input.accountNumber.slice(-4), country: 'US' };
    case 'foreign_account':
      return {
        secret: input.accountNumber,
        last4: input.accountNumber.slice(-4),
        country: input.country,
      };
  }
}

/** O que identifica a mesma conta no grupo, para não cadastrar duas vezes. */
export function treasuryFingerprintSource(input: TreasuryBankAccountInput): string {
  const { secret } = treasuryBankAccountSecret(input);
  switch (input.kind) {
    case 'br_account':
      return `${input.bankCode}|${input.branch}|${secret}`;
    case 'us_account':
      return `${input.routingNumber}|${secret}`;
    case 'foreign_account':
      return `${input.swiftBic}|${secret}`;
    case 'iban':
      return secret;
  }
}

/** A conta vale numa data: confirmada e dentro da vigência. */
export function treasuryAccountUsable(
  a: { status: string; validFrom: string; validUntil: string | null },
  on: string,
): boolean {
  return a.status === 'active' && a.validFrom <= on && (!a.validUntil || a.validUntil >= on);
}
